import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import styled from 'styled-components';

import Legend from './components/Legend';
import Navigation from './components/navigation/Navigation';
import StateMap from './components/StateMap';
import USMap from './components/USMap';
import { StateProvider } from './context';
import domainJson from './data/domain.json';
import { useData } from './hooks/useData';
import { ActiveStateOrUSId } from './types';

const AppWrapper = styled.div`
	color: #333;
	font-family: 'Roboto Flex', sans-serif;
	margin: 0 auto;
	max-width: 1024px;

	* {
		box-sizing: border-box;
	}
`;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 60,
			gcTime: 1000 * 60 * 60,
		},
	},
});

const App = () => {
	const { isLoading, data, error } = useData();
	const [year, setYear] = useState(2026);
	const [activeState, setActiveState] = useState<ActiveStateOrUSId>(0);

	const domain = domainJson as number[];
	const scale = 1560;
	const xScale = 1200;
	const yScale = 800;

	if (isLoading) {
		return <p>Loading...</p>;
	}

	if (error || !data) {
		return <p>Error loading data.</p>;
	}

	return (
		data && (
			<AppWrapper className="App">
				<Navigation
					activeState={activeState}
					setActiveState={setActiveState}
					year={year}
					setYear={setYear}
					startYear={2026}
					endYear={2035}
				/>
				<Legend domain={domain} steps={19} year={year} />

				{activeState !== 0 ? (
					<StateMap
						id={activeState}
						data={data}
						year={year}
						activeState={activeState}
						updateActiveState={setActiveState}
						domain={domain}
					/>
				) : (
					<USMap
						data={data}
						year={year}
						updateActiveState={setActiveState}
						domain={domain}
						scale={scale}
						xScale={xScale}
						yScale={yScale}
					/>
				)}
			</AppWrapper>
		)
	);
};

export default () => (
	<QueryClientProvider client={queryClient}>
		<StateProvider>
			<App />
		</StateProvider>
	</QueryClientProvider>
);
